import { supabase } from './supabase';
import { LandlordAvailability, MeetingRequest, DBMeeting } from '../types';
import { toast } from 'sonner@2.0.3';
import { format, addDays } from 'date-fns';

// --- Availability CRUD ---

export async function getLandlordAvailability(landlordId: string, apartmentId?: string): Promise<LandlordAvailability[]> {
  let query = supabase
    .from('landlord_availability')
    .select('*')
    .eq('landlord_id', landlordId);

  // Filter by apartment if provided
  if (apartmentId) {
    query = query.eq('apartment_id', apartmentId);
  }

  // Filter to next 14 days (date-specific availability)
  const today = format(new Date(), 'yyyy-MM-dd');
  const endDate = format(addDays(new Date(), 14), 'yyyy-MM-dd');
  query = query.gte('date', today).lte('date', endDate);

  const { data, error } = await query.order('date', { ascending: true }).order('start_time', { ascending: true });

  if (error) {
    console.error('Error fetching availability:', error);
    toast.error('Failed to load availability');
    return [];
  }

  return data || [];
}

export async function saveLandlordAvailability(
  landlordId: string, 
  apartmentId: string,
  availabilities: Omit<LandlordAvailability, 'id' | 'created_at' | 'landlord_id' | 'apartment_id'>[]
) {
  // 1. Calculate the 14-day range (same as what the UI manages)
  const today = format(new Date(), 'yyyy-MM-dd');
  const endDate = format(addDays(new Date(), 14), 'yyyy-MM-dd');

  // 2. Delete all unbooked availability for this apartment in the 14-day range
  // This ensures that if a user unchecks all slots for a date, it gets cleared
  const { error: deleteError } = await supabase
    .from('landlord_availability')
    .delete()
    .eq('landlord_id', landlordId)
    .eq('apartment_id', apartmentId)
    .eq('is_booked', false)
    .gte('date', today)
    .lte('date', endDate);

  if (deleteError) {
    console.error('Error clearing old availability:', deleteError);
    throw deleteError;
  }

  // 3. Insert new availability (only if there are slots to insert)
  if (availabilities.length === 0) return;

  const records = availabilities.map(a => ({
    landlord_id: landlordId,
    apartment_id: apartmentId,
    date: a.date,
    start_time: a.start_time,
    end_time: a.end_time,
    is_booked: false,
    booked_by_meeting_id: null
  }));

  const { error: insertError } = await supabase
    .from('landlord_availability')
    .insert(records);

  if (insertError) {
    console.error('Error saving availability:', insertError);
    throw insertError;
  }
}

// --- Meeting CRUD ---

export async function getMeetingsForLandlord(landlordId: string): Promise<MeetingRequest[]> {
  const { data, error } = await supabase
    .from('meetings')
    .select('*')
    .eq('landlord_id', landlordId)
    .order('start_time', { ascending: true });

  if (error) {
    console.error('Error fetching meetings:', error);
    return [];
  }

  // Transform DB shape to UI shape
  return (data || []).map((m: DBMeeting): MeetingRequest => {
    const start = new Date(m.start_time);
    return {
      id: m.id,
      apartmentId: m.apartment_id,
      user: m.renter_name || 'Interested Renter', // Use stored renter name, fallback to "Interested Renter"
      type: m.type,
      date: start.toLocaleDateString(),
      time: start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: m.status,
      message: m.message || '',
      start_time: m.start_time,
      end_time: m.end_time,
      renter_id: m.renter_id
    };
  });
}

export async function getMeetingsForRenter(renterId: string) {
  const now = new Date().toISOString();
  
  const { data, error } = await supabase
    .from('meetings')
    .select('*, apartment:apartments(*)')
    .eq('renter_id', renterId)
    .in('status', ['pending', 'accepted'])
    .gte('start_time', now)
    .order('start_time', { ascending: true });

  if (error) {
    console.error('Error fetching renter meetings:', error);
    return [];
  }

  return data || [];
}

export async function updateMeetingStatus(meetingId: string, status: 'accepted' | 'declined') {
  const { error } = await supabase
    .from('meetings')
    .update({ status })
    .eq('id', meetingId);

  if (error) {
    console.error('Error updating meeting:', error);
    throw error;
  }
}

export async function requestMeeting(
  apartmentId: string, 
  landlordId: string, 
  date: Date, 
  message: string
) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error("User not logged in");
  }

  const start_time = date.toISOString();
  const end_time = new Date(date.getTime() + 15 * 60000).toISOString(); // 15 min duration

  // Get the user's name from user metadata, fallback to "Interested Renter" if not set
  const renterName = user.user_metadata?.name || 'Interested Renter';

  // Insert meeting
  const { data: meetingData, error } = await supabase
    .from('meetings')
    .insert({
      apartment_id: apartmentId,
      landlord_id: landlordId,
      renter_id: user.id,
      renter_name: renterName,
      start_time,
      end_time,
      status: 'pending',
      type: 'First Tour',
      message
    })
    .select()
    .single();

  if (error) {
    console.error("Error requesting meeting:", error);
    throw error;
  }

  // Mark the availability slot as booked
  if (meetingData) {
    await bookSlot(meetingData.id, date);
  }
}

// --- Logic for Finding Free Slots ---

export async function getAvailableSlots(landlordId: string, apartmentId: string, date: Date): Promise<Date[]> {
  // 1. Get Availability Rules for the specific date AND apartment (date-specific now)
  const dateStr = format(date, 'yyyy-MM-dd');
  const { data: rules } = await supabase
    .from('landlord_availability')
    .select('*')
    .eq('landlord_id', landlordId)
    .eq('apartment_id', apartmentId)
    .eq('date', dateStr)
    .eq('is_booked', false); // Only get unbooked slots

  if (!rules || rules.length === 0) return [];

  // 2. Generate 15-min slots from available ranges
  const slots: Date[] = [];
  
  rules.forEach(rule => {
    // Parse start/end time strings (e.g. "09:00:00")
    const [startH, startM] = rule.start_time.split(':').map(Number);
    const [endH, endM] = rule.end_time.split(':').map(Number);

    const slotStart = new Date(date);
    slotStart.setHours(startH, startM, 0, 0);
    
    const ruleEnd = new Date(date);
    ruleEnd.setHours(endH, endM, 0, 0);

    // Create 15-min slots
    while (slotStart.getTime() + 15 * 60000 <= ruleEnd.getTime()) {
      slots.push(new Date(slotStart));

      // Increment by 15 mins
      slotStart.setMinutes(slotStart.getMinutes() + 15);
    }
  });

  return slots;
}

// --- Slot Booking Management ---

export async function bookSlot(meetingId: string, startTime: Date) {
  const dateStr = format(startTime, 'yyyy-MM-dd');
  const timeStr = format(startTime, 'HH:mm:ss');

  // Update availability slots that contain this time
  const { error } = await supabase
    .from('landlord_availability')
    .update({
      is_booked: true,
      booked_by_meeting_id: meetingId
    })
    .eq('date', dateStr)
    .lte('start_time', timeStr)
    .gt('end_time', timeStr);

  if (error) {
    console.error('Error booking slot:', error);
    throw error;
  }
}

export async function freeSlot(meetingId: string) {
  const { error } = await supabase
    .from('landlord_availability')
    .update({
      is_booked: false,
      booked_by_meeting_id: null
    })
    .eq('booked_by_meeting_id', meetingId);

  if (error) {
    console.error('Error freeing slot:', error);
    throw error;
  }
}

// --- Cancellation & Rescheduling ---

export async function cancelMeeting(
  meetingId: string,
  _currentStatus: 'pending' | 'accepted' | 'declined'
) {
  // Note: currentStatus is kept for API compatibility but not currently used
  // Get current user to record who cancelled
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  // Always soft delete (mark as cancelled) so landlord retains history
  const { error } = await supabase
    .from('meetings')
    .update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      cancelled_by: user.id
    })
    .eq('id', meetingId);

  if (error) {
    console.error('Error cancelling meeting:', error);
    throw error;
  }

  // Free the slot
  await freeSlot(meetingId);
}

export async function rescheduleMeeting(
  meetingId: string,
  apartmentId: string,
  landlordId: string,
  newStartTime: Date,
  message?: string
) {
  // 1. Delete old meeting (overrides previous request)
  const { error: deleteError } = await supabase
    .from('meetings')
    .delete()
    .eq('id', meetingId);

  if (deleteError) {
    console.error('Error deleting old meeting:', deleteError);
    throw deleteError;
  }

  // 2. Create new meeting with pending status (landlord must re-approve)
  return await requestMeeting(apartmentId, landlordId, newStartTime, message || '');
}

// --- Google Calendar Integration ---

export function generateGoogleCalendarLink(
  title: string,
  startTime: Date,
  endTime: Date,
  description?: string,
  location?: string
): string {
  const formatDate = (date: Date) => 
    format(date, "yyyyMMdd'T'HHmmss'Z'");
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatDate(startTime)}/${formatDate(endTime)}`,
    details: description || '',
    location: location || ''
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
